import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCartItems, updateCartItem, deleteCartItem } from '../api/cartApi';
import { isLoggedIn } from '../utils/tokenUtils';
import styles from './CartPage.module.css';
import { useCart } from '../context/CartContext';
import { useModal } from '../context/ModalContext';
import { useLocation } from 'react-router-dom';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function CartPage() {
    const navigate = useNavigate();
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [checkedItems, setCheckedItems] = useState([]);
    const { refreshCartCount } = useCart();
    const { showAlert, showConfirm } = useModal();
    const location = useLocation();

    useEffect(() => {
        if (!isLoggedIn()) { navigate('/login', { state: { from: location.pathname } }); return; }
        loadCartItems();
    }, []);

    const loadCartItems = async () => {
        try {
            const response = await getCartItems();
            setCartItems(response.data);
            setCheckedItems(response.data.map(item => item.cartId));
        } catch (err) {
            console.error('장바구니 로딩 실패', err);
        } finally {
            setLoading(false);
            refreshCartCount();
        }

    };

    const handleQuantityChange = async (cartId, newQuantity) => {
        if (newQuantity < 1) return;
        try {
            await updateCartItem(cartId, newQuantity);
            setCartItems(cartItems.map(item =>
                item.cartId === cartId
                    ? { ...item, quantity: newQuantity, totalPrice: (item.price + item.additionalPrice) * newQuantity }
                    : item
            ));
        } catch { console.error('수량 변경 실패'); }
    };

    const handleDelete = async (cartId) => {
        try {
            await deleteCartItem(cartId);
            setCartItems(cartItems.filter(item => item.cartId !== cartId));
            setCheckedItems(checkedItems.filter(id => id !== cartId));
            refreshCartCount();
        } catch { await showAlert('삭제에 실패했습니다.', 'error'); }
    };

    const handleDeleteChecked = async () => {
        if (checkedItems.length === 0) return;
        const ok = await showConfirm('선택한 상품을 삭제하시겠습니까?');
        if (!ok) return;
        try {
            await Promise.all(checkedItems.map(cartId => deleteCartItem(cartId)));
            setCartItems(cartItems.filter(item => !checkedItems.includes(item.cartId)));
            setCheckedItems([]);
            refreshCartCount();
        } catch { await showAlert('삭제에 실패했습니다.', 'error'); }
    };

    const handleCheckItem = (cartId) => {
        setCheckedItems(prev =>
            prev.includes(cartId) ? prev.filter(id => id !== cartId) : [...prev, cartId]
        );
    };

    const handleCheckAll = () => {
        if (checkedItems.length === cartItems.length) setCheckedItems([]);
        else setCheckedItems(cartItems.map(item => item.cartId));
    };

    const totalPrice = cartItems
        .filter(item => checkedItems.includes(item.cartId))
        .reduce((sum, item) => sum + item.totalPrice, 0);

    const handleOrder = () => {
        if (checkedItems.length === 0) { showAlert('주문할 상품을 선택해주세요.', 'warning'); return; }
        navigate('/order');
    };

    if (loading) return <p className={styles.loading}>로딩 중...</p>;

    return (
        <div className={styles.container}>
            <h2 className={styles.title}>장바구니</h2>

            {cartItems.length === 0 ? (
                <div className={styles.empty}>
                    <p>장바구니가 비어있습니다.</p>
                    <button onClick={() => navigate('/products')} className={styles.shopButton}>쇼핑 계속하기</button>
                </div>
            ) : (
                <>
                    {/* 전체 선택 */}
                    <div className={styles.selectAll}>
                        <label className={styles.checkLabel}>
                            <input type="checkbox"
                                checked={checkedItems.length === cartItems.length}
                                onChange={handleCheckAll} />
                            전체 선택 ({checkedItems.length}/{cartItems.length})
                        </label>
                        <button className={styles.deleteSelectedBtn} onClick={handleDeleteChecked}>
                            선택 삭제
                        </button>
                    </div>

                    <div className={styles.cartList}>
                        {cartItems.map(item => (
                            <div key={item.cartId} className={`${styles.cartItem} ${!checkedItems.includes(item.cartId) ? styles.unchecked : ''}`}>
                                <input type="checkbox"
                                    checked={checkedItems.includes(item.cartId)}
                                    onChange={() => handleCheckItem(item.cartId)}
                                    className={styles.checkbox} />
                                <img src={`${API_URL}${item.imageUrl}`}
                                    alt={item.productName} className={styles.productImage}
                                    onClick={() => navigate(`/products/${item.productId}`)} />
                                <div className={styles.productInfo}>
                                    <p className={styles.productName}
                                        onClick={() => navigate(`/products/${item.productId}`)}>
                                        {item.productName}
                                    </p>
                                    {/* 옵션 표시 */}
                                    {item.optionName && (
                                        <p className={styles.optionText}>
                                            {item.optionName}: {item.optionValue}
                                            {item.additionalPrice > 0 && ` (+${item.additionalPrice.toLocaleString()}원)`}
                                        </p>
                                    )}
                                    <p className={styles.productPrice}>
                                        {(item.price + item.additionalPrice).toLocaleString()}원
                                    </p>
                                </div>
                                <div className={styles.quantityControl}>
                                    <button onClick={() => handleQuantityChange(item.cartId, item.quantity - 1)}>-</button>
                                    <span>{item.quantity}</span>
                                    <button onClick={() => handleQuantityChange(item.cartId, item.quantity + 1)}>+</button>
                                </div>
                                <p className={styles.totalPrice}>{item.totalPrice.toLocaleString()}원</p>
                                <button className={styles.deleteButton} onClick={() => handleDelete(item.cartId)}>삭제</button>
                            </div>
                        ))}
                    </div>

                    {/* 주문 요약 */}
                    <div className={styles.summary}>
                        <div className={styles.summaryRow}>
                            <span>선택 상품 ({checkedItems.length}개)</span>
                            <span>{totalPrice.toLocaleString()}원</span>
                        </div>
                        <div className={styles.summaryRow}>
                            <span>배송비</span>
                            <span>무료</span>
                        </div>
                        <hr />
                        <div className={styles.summaryTotal}>
                            <span>총 결제금액</span>
                            <span className={styles.totalAmount}>{totalPrice.toLocaleString()}원</span>
                        </div>
                        <button className={styles.orderButton} onClick={handleOrder}>
                            주문하기 ({checkedItems.length}개)
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

export default CartPage;